import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Ban, Copy, ShieldCheck, X } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet } from '@/components/ui/sheet';
import { TabBar } from '@/components/ui/tab-bar';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { phoneStatus, phoneText } from '@/lib/mobile-link-phone';
import { activityDetails, activityLabel, humanizeCode, suspendReasonLabel } from '@/lib/mobile-link-labels';
import { formFromRow, type CatalogEntry } from './link-form';
import { LinkSummary, useLinkSummary } from './LinkSummary';
import { STATE_TONE, creatorName, type ActivityRow, type MobileLinkRow } from './types';

export type DetailsTab = 'overview' | 'activity';

interface Props {
  link: MobileLinkRow | null;
  tab: DetailsTab;
  onTab: (tab: DetailsTab) => void;
  onClose: () => void;
  catalog: CatalogEntry[];
  locations: { id: string; name: string }[];
  isAdmin: boolean;
  canManage: boolean;
  onRevoke: (l: MobileLinkRow) => void;
  onReactivate: (l: MobileLinkRow) => void;
  onCreateSimilar: (l: MobileLinkRow) => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{children}</dd>
    </div>
  );
}

/** Right-side drawer: Overview (what the link can and cannot do) and Activity (audit events) of one link. */
export function LinkDetailsSheet({ link, tab, onTab, onClose, catalog, locations, isAdmin, canManage, onRevoke, onReactivate, onCreateSimilar }: Props) {
  const { t } = useTranslation('mobileLinks');

  const { data: activity = [], isLoading: activityLoading } = useQuery<ActivityRow[]>({
    queryKey: ['mobile-link-activity', link?.id],
    queryFn: () => api.get(`/mobile-links/${link?.id}/activity`).then((r) => r.data),
    enabled: !!link && tab === 'activity',
  });

  const form = useMemo(() => formFromRow(link ?? { name: '', scopes: [] }), [link]);
  const expiresAt = useMemo(() => (link ? new Date(link.expiresAt) : null), [link]);
  const locationName = link?.locationId ? locations.find((l) => l.id === link.locationId)?.name ?? null : null;
  const summary = useLinkSummary(form, catalog, expiresAt, locationName);

  const finished = link ? link.state === 'revoked' || link.state === 'expired' || link.state === 'used_up' : false;
  const phoneState = link ? phoneStatus(link.state === 'active' ? link : { lastSeenAt: link.lastSeenAt }) : null;
  const phoneNow = !phoneState
    ? '-'
    : phoneState.kind === 'active'
      ? `${t('phoneActiveNow')} · ${phoneText(t, phoneState)}`
      : phoneState.kind === 'never' && link?.lastUsedAt
        ? t('detailsLastOpened', { date: formatDate(link.lastUsedAt) })
        : phoneText(t, phoneState);

  return (
    <Sheet open={!!link} onClose={onClose} labelledBy="link-details-title" testId="link-details" className="sm:max-w-[720px]">
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-start justify-between gap-3 border-b p-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 id="link-details-title" className="max-w-full truncate text-base font-semibold" title={link?.name}>
              {link?.name}
            </h2>
            {link && <Badge variant={STATE_TONE[link.state]}>{t(`state_${link.state}`)}</Badge>}
          </div>
          <Button variant="ghost" size="sm" data-sheet-initial onClick={onClose} aria-label={t('detailsClose')} className="min-h-[44px] min-w-[44px] shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <TabBar
          bordered={false}
          className="border-b px-4 py-2"
          activeTab={tab}
          onTabChange={(k) => onTab(k as DetailsTab)}
          tabs={[
            { key: 'overview', label: t('detailsOverview') },
            { key: 'activity', label: t('detailsActivity') },
          ]}
        />

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {link && tab === 'overview' && (
            <div className="space-y-6">
              {link.state === 'suspended' && link.suspendedReason && (
                <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning-text">{suspendReasonLabel(t, link.suspendedReason)}</p>
              )}
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label={t('detailsPhoneNow')}>{phoneNow}</Field>
                <Field label={t('detailsCreatedBy')}>{creatorName(link)}</Field>
                <Field label={t('detailsLocation')}>{locationName ?? t('locationDefault')}</Field>
                <Field label={t('detailsCreated')}>{link.createdAt ? formatDate(link.createdAt) : '-'}</Field>
                <Field label={t('detailsExpires')}>{formatDate(link.expiresAt)}</Field>
                <Field label={t('detailsOpened')}>{link.useCount} / {link.maxUses ?? t('unlimited')}</Field>
                <Field label={t('detailsLink')}><span dir="ltr" className="font-mono text-xs">{link.tokenPrefix}…</span></Field>
              </dl>
              <section aria-label={t('summaryTitle')} className="rounded-lg border p-4">
                <h3 className="mb-3 text-sm font-semibold">{t('summaryTitle')}</h3>
                <LinkSummary summary={summary} />
              </section>
              <p className="text-xs text-muted-foreground">{t('detailsChangeNote')}</p>
            </div>
          )}

          {link && tab === 'activity' &&
            (activityLoading ? (
              <SkeletonTable rows={4} cols={2} />
            ) : activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('activityEmpty')}</p>
            ) : (
              <ul className="space-y-3">
                {activity.map((a) => (
                  <li key={a.id} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{activityLabel(t, a.action)}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(a.createdAt)}</span>
                    </div>
                    {a.newValues?.reason && (
                      <p className="mt-1 text-xs">
                        {a.action === 'SUSPEND' ? suspendReasonLabel(t, a.newValues.reason) : t(`reason_${a.newValues.reason}`, { defaultValue: humanizeCode(a.newValues.reason) })}
                      </p>
                    )}
                    {activityDetails(t, a, formatCurrency).map((d) => <p key={d} className="mt-1 text-xs">{d}</p>)}
                    <p className="mt-1 text-xs text-muted-foreground" dir="ltr">{[a.ipAddress, a.userAgent?.slice(0, 60)].filter(Boolean).join(' · ')}</p>
                  </li>
                ))}
              </ul>
            ))}
        </div>

        {link && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t p-4">
            {canManage ? (
              <Button type="button" variant="outline" className="min-h-[44px] gap-2" onClick={() => onCreateSimilar(link)}>
                <Copy className="h-4 w-4" aria-hidden /> {t('createSimilar')}
              </Button>
            ) : <span />}
            <div className="flex flex-wrap gap-2">
              {!finished && isAdmin && link.state === 'suspended' && (
                <Button type="button" variant="outline" className="min-h-[44px] gap-2" onClick={() => onReactivate(link)}>
                  <ShieldCheck className="h-4 w-4" aria-hidden /> {t('reactivate')}
                </Button>
              )}
              {!finished && canManage && (
                <Button type="button" variant="outline" className="min-h-[44px] gap-2 text-destructive" onClick={() => onRevoke(link)}>
                  <Ban className="h-4 w-4" aria-hidden /> {t('revoke')}
                </Button>
              )}
              <Button type="button" variant="outline" className="min-h-[44px]" onClick={onClose}>{t('detailsClose')}</Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}
